import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo
} from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { resetRedux } from 'redux/actions'
import { useMutation } from '@apollo/client';
import { client } from 'graphql/apolloClient';
import { LOGIN } from 'graphql/service';
import jwt_decode from "jwt-decode"
import Cookies from 'js-cookie';
import { debouncedErrorMessage } from 'utils/messageUtil';
import { message } from 'antd';

/**
 * A custom hook for auth includes:
 * - authProvide
 * - useAuthContext
 */

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {

  const history = useHistory();
  const dispatch = useDispatch()
  const [ validToken, setValidToken ] = useState(Cookies.get('token') || '')

  useEffect(() => {
    const token = Cookies.get('token') || ''
    if(token) {
      setValidToken(token)
    } else {
      setValidToken('')
      Cookies.remove('token')
      Cookies.remove('userId')
      Cookies.remove('fullName')
    }
  }, [])

  const [login, { loading: loginLoading, error: loginError }]  = useMutation(
    LOGIN,
    { 
      onCompleted: (loginData) => {
        if(loginData) {
          const token = loginData.authenticate?.accessToken        
          const decoded = jwt_decode(token)
          if(decoded.user_type === 'TENANT') {
            const content = 'Unauthorized User Type!'
            debouncedErrorMessage(content);
            userLogout()
          } else {
            Cookies.set('userId', decoded.sub, { expires: new Date(decoded.exp * 1000) });
            Cookies.set('token', token, { expires: new Date(decoded.exp * 1000) });
            Cookies.set('fullName', decoded.username, { expires: new Date(decoded.exp * 1000)});
            setValidToken(token)
            history.push(localStorage.getItem('previousPath') || '/')
          }
        }
      },
      onError: (error) => {
        if (!error.networkError) {
          let content = error.message
          if(error.message.includes('Validation')) {
              content = JSON.stringify(error.graphQLErrors[0]?.extensions?.exception)
          }
          debouncedErrorMessage(content);
        }
      }
    }
  )

  const userLogout = useCallback(() => {
    Object.keys(Cookies.get()).forEach((cookieName) => {
      Cookies.remove(cookieName);
    })
    localStorage.clear()
    client.clearStore()
    setValidToken('')
    dispatch(resetRedux())
    message.success('Log out successfully!')
    history.push('/login')
  }, [dispatch, history]);

  const userLogin = useCallback((email, password, setPasswordError) => {
    login({
      variables: {
        input: {
          login: email,
          password: password
        }
      }
    })

    if (loginError) {
      setPasswordError(true)
    }
  }, [login, loginError]);

  const memoedValue = useMemo(() => ({
    validToken,
    userLogin,
    loginLoading,
    userLogout
  }), [validToken, userLogin, loginLoading, userLogout]);

  return (
    <AuthContext.Provider value={memoedValue}>
      {children}
    </AuthContext.Provider>
  )
}


const useAuth = () => {
  return useContext(AuthContext);
}  

export default useAuth
